// src/lib/month/metrics.ts
import { type MonthKey, monthShort } from "@/lib/month/month";
import type { IsoDate } from "@/lib/time/dates";
import { formatHours } from "@/lib/time/rules";
import { missingHours, openPastWeeks, type SignOffLike } from "@/lib/time/signoff-rules";

/**
 * Home and Guidance maths for one person-month (spec "Definitions"). One utilization base:
 * available = capacity - PTO (DECISIONS 2026-10-15).
 */

export interface MonthTotals {
  /** PersonPeriod.HourCapacity; null when the person has no PersonPeriod for the month. */
  capacity: number | null;
  /** Non-productive hours (vacation, sick, holiday, unpaid, other). */
  pto: number;
  /** Productive hours: what "hours logged" means on these pages. */
  productive: number;
  /** Hours with a BillableAmount. */
  billable: number;
  /** Sum of DemandPlan.GuidanceQuantity. */
  planned: number;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/** "True capacity": capacity - PTO. */
export function availableHours(m: MonthTotals): number | null {
  return m.capacity === null ? null : round2(m.capacity - m.pto);
}

/** Productive / (capacity - PTO); null when that base is missing or not positive. */
export function utilization(m: MonthTotals): number | null {
  const available = availableHours(m);
  return available === null || available <= 0 ? null : m.productive / available;
}

export function formatPercent(ratio: number | null): string {
  return ratio === null ? "" : `${Math.round(ratio * 100)}%`;
}

export interface CapacityBar {
  planned: number;
  pto: number;
  capacity: number;
  /** planned + PTO - capacity: positive = over, negative = under. Equals planned - available. */
  delta: number;
  plannedPct: number;
  ptoPct: number;
  markerPct: number;
}

/** Room to the right of the longest bar or marker. */
const HEADROOM = 1.08;

export function capacityBar(m: MonthTotals): CapacityBar | null {
  if (m.capacity === null) return null;
  const load = m.planned + m.pto;
  const scale = Math.max(load, m.capacity, 1) * HEADROOM;
  const pct = (v: number) => (v / scale) * 100;
  return {
    planned: m.planned,
    pto: m.pto,
    capacity: m.capacity,
    delta: round2(load - m.capacity),
    plannedPct: pct(m.planned),
    ptoPct: pct(m.pto),
    markerPct: pct(m.capacity),
  };
}

export function capacityChip(bar: CapacityBar): { tone: "over" | "under"; text: string } | null {
  if (bar.delta > 0) return { tone: "over", text: `${formatHours(bar.delta)} hrs over` };
  if (bar.delta < 0) return { tone: "under", text: `${formatHours(-bar.delta)} hrs under` };
  return null;
}

export interface PlanLike {
  demandPersonId: string;
  demandName: string;
  planned: number | null;
  logged: number;
}

export interface PlanBarRow {
  id: string;
  name: string;
  /** "9 of 40 hrs". */
  label: string;
  /** Logged / planned, capped at 1. */
  ratio: number;
  over: boolean;
}

/** Home's "Guidance vs logged": demands with guidance, most guidance first. */
export function planVsLogged(rows: PlanLike[], limit = 6): PlanBarRow[] {
  return rows
    .filter((r) => (r.planned ?? 0) > 0)
    .sort((a, b) => (b.planned ?? 0) - (a.planned ?? 0) || a.demandName.localeCompare(b.demandName))
    .slice(0, limit)
    .map((r) => {
      const planned = r.planned ?? 0;
      return {
        id: r.demandPersonId,
        name: r.demandName,
        label: `${formatHours(r.logged)} of ${formatHours(planned)} hrs`,
        ratio: Math.min(r.logged / planned, 1),
        over: r.logged > planned,
      };
    });
}

/** Home's trend chart: hours by month, each month's label carrying its utilization. */
export function trendRows(
  months: Array<MonthTotals & { month: MonthKey }>,
  percent: (ratio: number | null) => string,
) {
  return months.map((m) => {
    const rate = percent(utilization(m));
    return {
      label: rate ? `${monthShort(m.month)} ${rate}` : monthShort(m.month),
      productive: m.productive,
      pto: m.pto,
    };
  });
}

export interface HomeAlerts {
  weeksToSignOff: number;
  missingHours: number;
}

/**
 * Home's attention tiles: ended weeks still open, and the hours missing from them (capacity -
 * every hour, PTO included; Time Entry's missingHours). Each tile hides at 0.
 */
export function homeAlerts(rows: SignOffLike[], today: IsoDate): HomeAlerts {
  const open = openPastWeeks(rows, today);
  return {
    weeksToSignOff: open.length,
    missingHours: round2(open.reduce((sum, w) => sum + missingHours(w), 0)),
  };
}

/** A KPI card's number as Incentyv stored it: projected first (as Vinyl), else actual. */
export function kpiValue(card: {
  actualDisplay: string | null;
  projectedDisplay: string | null;
}): { value: string; projected: boolean } | null {
  const projected = card.projectedDisplay?.trim() ?? "";
  if (projected) return { value: projected, projected: true };
  const actual = card.actualDisplay?.trim() ?? "";
  return actual ? { value: actual, projected: false } : null;
}

type AttentionTile = { kind: "weeks" | "missing"; value: number };

/** The attention tiles to show, each hidden at 0 (D10); none while the sign-offs load. */
export function attentionTiles(alerts: HomeAlerts | null): AttentionTile[] {
  const tiles: AttentionTile[] = [];
  if (alerts && alerts.weeksToSignOff > 0)
    tiles.push({ kind: "weeks", value: alerts.weeksToSignOff });
  if (alerts && alerts.missingHours > 0)
    tiles.push({ kind: "missing", value: alerts.missingHours });
  return tiles;
}

/** The KPI cards to show: a line Incentyv stored no value for is dropped, never shown as 0. */
export function visibleKpis<
  T extends { actualDisplay: string | null; projectedDisplay: string | null },
>(cards: T[]): Array<{ card: T; shown: { value: string; projected: boolean } }> {
  const out: Array<{ card: T; shown: { value: string; projected: boolean } }> = [];
  for (const card of cards) {
    const shown = kpiValue(card);
    if (shown) out.push({ card, shown });
  }
  return out;
}

/**
 * The selected month's row of a summary. While the next month loads (`isPlaceholder`, the
 * previous answer kept on screen) a month missing from it shows the previous selection, the
 * answer's last month, instead of blanking the card.
 */
export function monthRow<R extends { month: MonthKey }>(
  view: { months: R[] } | undefined,
  month: MonthKey,
  isPlaceholder: boolean,
): R | null {
  if (!view) return null;
  const row = view.months.find((m) => m.month === month);
  if (row) return row;
  return isPlaceholder ? (view.months.at(-1) ?? null) : null;
}
